import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../App';

// Context for family/tenant management
const FamilyContext = createContext(null);

export const useFamily = () => {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return context;
};

export const FamilyProvider = ({ children, user, setUser }) => {
  const [families, setFamilies] = useState([]);
  const [currentFamily, setCurrentFamily] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsFamilySetup, setNeedsFamilySetup] = useState(false);

  // Fetch families and current family details
  const fetchFamilyData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Get user's families
      const familiesRes = await api.get('/families');
      setFamilies(familiesRes.data);

      if (familiesRes.data.length === 0) {
        setNeedsFamilySetup(true);
        setLoading(false);
        return;
      }

      // Get current family details
      const currentRes = await api.get('/families/current');
      setCurrentFamily(currentRes.data);

      // Get plan details
      const planRes = await api.get('/families/current/plan');
      setPlanDetails(planRes.data);

      setNeedsFamilySetup(false);
    } catch (error) {
      console.error('Error fetching family data:', error);
      if (error.response?.status === 403) {
        setNeedsFamilySetup(true);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchFamilyData();
  }, [fetchFamilyData]);

  // Create a new family
  const createFamily = async (name) => {
    try {
      const res = await api.post('/families', { name });
      await fetchFamilyData();
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  // Switch to a different family
  const switchFamily = async (familyId) => {
    try {
      const res = await api.post(`/auth/switch-family/${familyId}`);
      // Update user's active family
      if (setUser) {
        setUser(prev => ({ ...prev, active_family_id: familyId }));
      }
      await fetchFamilyData();
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  // Invite a member
  const inviteMember = async (email, role = 'member') => {
    try {
      const res = await api.post('/invites', { email, role });
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  // Get pending invites
  const getPendingInvites = async () => {
    try {
      const res = await api.get('/invites');
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  // Accept an invite
  const acceptInvite = async (code) => {
    try {
      const res = await api.post(`/invites/${code}/accept`);
      await fetchFamilyData();
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  // Get family members
  const getFamilyMembers = async () => {
    try {
      const res = await api.get('/families/current/members');
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  // Check if user is admin
  const isAdmin = currentFamily?.your_role === 'admin';

  // Check if premium
  const isPremium = planDetails?.is_premium || false;

  // Check feature availability
  const canUseFeature = (featureName) => {
    if (!planDetails?.features) return true;
    return planDetails.features[featureName] !== false;
  };

  const value = {
    // State
    families,
    currentFamily,
    planDetails,
    loading,
    needsFamilySetup,
    isAdmin,
    isPremium,
    
    // Actions
    createFamily,
    switchFamily,
    inviteMember,
    acceptInvite,
    getPendingInvites,
    getFamilyMembers,
    refreshFamilyData: fetchFamilyData,
    canUseFeature,
  };

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  );
};

export default FamilyContext;
